import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Zap,
  Users,
  TrendingUp,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const Campaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    pendingCampaigns: 0,
    approvedCampaigns: 0,
    rejectedCampaigns: 0,
    activeCampaigns: 0,
    completedCampaigns: 0,
    totalParticipants: 0,
    totalTokensDistributed: 0,
    totalTokenPoolAllocated: 0,
    averageEngagementRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem("campaignsPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [selectedFilter, setSelectedFilter] = useState(() => {
    if (location.state?.filter) {
      return location.state.filter;
    }
    const saved = sessionStorage.getItem("campaignsFilter");
    return saved ? saved : "All";
  });

  // Save filter to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("campaignsFilter", selectedFilter);
  }, [selectedFilter]);

  // Save page to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("campaignsPage", currentPage.toString());
  }, [currentPage]);

  // Check for filter in location state on component mount
  useEffect(() => {
    if (location.state?.filter) {
      setSelectedFilter(location.state.filter);
      setCurrentPage(1);
    }
  }, [location.state?.filter]);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    fetchStats();
    fetchCampaigns();
  }, [currentPage, selectedFilter, navigate]);

  const fetchStats = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await axios.get(
        `${API_BASE_URL}/campaigns/admin/stats/overview`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (
        response.data.statusCode === 401 ||
        response.data.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      setStats(response.data.data);
    } catch (err) {
      if (
        err.response?.status === 401 ||
        err.response?.data?.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching stats:", err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = localStorage.getItem("accessToken");

      const params = new URLSearchParams({
        page: currentPage.toString(),
      });

      if (selectedFilter !== "All") {
        params.append("status", selectedFilter.toLowerCase());
      }

      const response = await axios.get(
        `${API_BASE_URL}/admin/merchants/campaigns?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (
        response.data.statusCode === 401 ||
        response.data.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      const data = response.data;

      const transformedCampaigns = data.campaigns.map((campaign) => {
        const formatDate = (isoString) => {
          if (!isoString) return { date: "N/A", time: "N/A" };
          const dateObj = new Date(isoString);
          const date = dateObj.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
          const time = dateObj.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          return { date, time };
        };

        const { date: startDate, time: startTime } = formatDate(
          campaign.startDate
        );
        const { date: endDate, time: endTime } = formatDate(campaign.endDate);

        const formatStatus = (status) => {
          if (!status) return "Draft";
          return status
            .replace(/_/g, " ")
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");
        };

        return {
          id: campaign._id,
          name: campaign.title,
          creator: `${campaign.merchantId.firstName} ${campaign.merchantId.lastName}`,
          phone: campaign.merchantId.email,
          startDate,
          startTime,
          endDate,
          endTime,
          revenue: `$${(campaign.rewardStructure.totalPool / 1000).toFixed(
            0
          )}K`,
          engagement: `${campaign.metrics.engagementRate}%`,
          status: formatStatus(campaign.status),
          approvalStatus: campaign.approvalStatus,
          avatar: `${campaign.merchantId.firstName[0] || ""}${
            campaign.merchantId.lastName[0] || ""
          }`.toUpperCase(),
        };
      });

      setCampaigns(transformedCampaigns);
      setPagination(data.pagination);
    } catch (err) {
      if (
        err.response?.status === 401 ||
        err.response?.data?.message === "Unauthorized"
      ) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching campaigns:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to load campaigns";
      setError(errorMessage);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignClick = (campaignId) => {
    navigate(`/campaign/${campaignId}`);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      Pending: isDarkMode
        ? "bg-orange-500/20 text-orange-400"
        : "bg-orange-100 text-orange-700",
      Live: isDarkMode
        ? "bg-green-500/20 text-green-400"
        : "bg-green-100 text-green-700",
      Completed: isDarkMode
        ? "bg-zinc-800 text-gray-400"
        : "bg-gray-200 text-gray-600",
      Rejected: isDarkMode
        ? "bg-red-500/20 text-red-400"
        : "bg-red-100 text-red-700",
    };
    return statusStyles[status] || "";
  };

  if (loading) {
    return (
      <div
        className={`flex flex-col min-h-screen ${
          isDarkMode ? "bg-black" : "bg-gray-100"
        }`}
      >
        <div
          className={`flex-grow flex items-center justify-center ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          <p className="text-xl">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-5 p-5">
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h2
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Campaign Management
          </h2>
        </div>

        <div className="flex gap-3">
          {["All", "Pending", "Approved", "Rejected"].map((filter) => (
            <button
              key={filter}
              onClick={() => handleFilterChange(filter)}
              className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                selectedFilter === filter
                  ? isDarkMode
                    ? "bg-orange-400 text-black"
                    : "bg-orange-500 text-white"
                  : isDarkMode
                  ? "bg-zinc-800 text-gray-300 hover:bg-zinc-700"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-300"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5 flex items-center gap-4`}
          >
            <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
              <FileText className="text-black" size={24} />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Total Campaigns
              </p>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {stats.totalCampaigns}
              </p>
            </div>
          </div>

          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5 flex items-center gap-4`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-200"
              }`}
            >
              <Clock
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                size={24}
              />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Pending
              </p>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {stats.pendingCampaigns}
              </p>
            </div>
          </div>

          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5 flex items-center gap-4`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-200"
              }`}
            >
              <CheckCircle
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                size={24}
              />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Approved
              </p>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {stats.approvedCampaigns}
              </p>
            </div>
          </div>

          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5 flex items-center gap-4`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-200"
              }`}
            >
              <XCircle
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                size={24}
              />
            </div>
            <div>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                Rejected
              </p>
              <p
                className={`text-2xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {stats.rejectedCampaigns}
              </p>
            </div>
          </div>
        </div>

        {/* Campaign Table */}
        <div
          className={`${
            isDarkMode ? "bg-black border-zinc-800" : "bg-white border-gray-200"
          } border rounded-xl p-5`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr
                  className={`border-b ${
                    isDarkMode ? "border-zinc-800" : "border-gray-200"
                  }`}
                >
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Campaign Name
                  </th>
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Creator
                  </th>
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Timeframe
                  </th>
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Exp. Revenue
                  </th>
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Exp. Engagement
                  </th>
                  <th
                    className={`text-left py-3 px-2 text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`text-center py-10 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      No campaigns found
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign) => (
                    <tr
                      key={campaign.id}
                      onClick={() => handleCampaignClick(campaign.id)}
                      className={`border-b cursor-pointer transition-colors ${
                        isDarkMode
                          ? "border-zinc-800 hover:bg-zinc-900"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <td className="py-4 px-2">
                        <p
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {campaign.name}
                        </p>
                      </td>
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {campaign.avatar}
                          </div>
                          <div>
                            <p
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {campaign.creator}
                            </p>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {campaign.phone}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="flex gap-4">
                          <div>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {campaign.startDate}
                            </p>
                            <p
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {campaign.startTime}
                            </p>
                          </div>
                          <div
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            —
                          </div>
                          <div>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {campaign.endDate}
                            </p>
                            <p
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {campaign.endTime}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td
                        className={`py-4 px-2 font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {campaign.revenue}
                      </td>
                      <td
                        className={`py-4 px-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {campaign.engagement}
                      </td>
                      <td className="py-4 px-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                            campaign.status
                          )}`}
                        >
                          {campaign.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
              isDarkMode ? "border-zinc-800" : "border-gray-200"
            }`}
          >
            <button
              onClick={() => setCurrentPage(pagination.page - 1)}
              disabled={!pagination.hasPrevPage}
              className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ChevronLeft size={20} />
            </button>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Page {pagination.page || 1} of {pagination.totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Campaigns;